import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Modal from 'antd/es/modal/Modal';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import { rest } from '@app/components/Api';
import { dateConvert } from '@app/components/Pages/PetMemorial/utils/dateConvert';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import LinkMore from '@app/components/UI/LinkMore/LinkMore';
import { TesAuthorCredits } from '@app/components/UI/VetTesCard/VetTesCard';
import imagePlaceholder from '@app/images/image-placeholder.png';
import { DatePost, MemorialsRes } from '@app/objects/PetMemorial';
import { VetTestimonialsRes } from '@app/objects/VetTestimonialsRes';
import { bodyOverflowRemove, bodyOverflowTransform } from '@app/utils/bodyOverflowTransform';

interface CurrentTestimonial extends VetTestimonialsRes {
	datePost: DatePost;
}

const TestimonialCardModal: React.FC = () => {
	const [openModal, setOpenModal] = React.useState<boolean>(false);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [errorMessage, setErrorMessage] = React.useState<string>('');
	const [currentTestimonial, setCurrentTestimonial] = React.useState<CurrentTestimonial>();
	const navigate = useNavigate();
	const params = useParams<{ id: string | undefined}>();

	const closeModal = () => {
		setOpenModal(false);
		bodyOverflowRemove();
		navigate('/veterinarian-testimonials');
	};

	const imageAltCapture = currentTestimonial && (
		currentTestimonial.user && `
			Lap of Love Veterinary Hospice ${`${currentTestimonial?.city?.trim() || ''}, `}
			${currentTestimonial?.state || ''} Testimonial for Dr. ${`${currentTestimonial?.user?.firstName || ''} `}
			${`${currentTestimonial?.user?.lastName || ''} `}
			${currentTestimonial?.firstName ? `by ${currentTestimonial.firstName} ${currentTestimonial?.lastName || ''}` : ''}
		`
	);

	React.useEffect(() => {
		if (params?.id) {
			bodyOverflowTransform(true);
			setCurrentTestimonial(undefined);
			setIsLoading(true);
			setOpenModal(true);
			setErrorMessage('');

			rest.get<VetTestimonialsRes>(`v1/testimonials/${params.id}`)
				.then((res) => {
					const datePost = dateConvert((res as unknown) as MemorialsRes).datePost;
					setCurrentTestimonial({ ...res, datePost });
				})
				.catch((err) => setErrorMessage(err))
				.finally(() => setIsLoading(false));
		}
	}, [params?.id]);

	return (
		<Modal
			wrapClassName="pet-mem-modal modal-wrap"
			open={openModal}
			onCancel={closeModal}
			width={1037}
			getContainer="#vet-testimonials"
		>
			<div className="pet-mem-modal__content">
				<div className="pet-mem-modal__container">
					{!isLoading ? (
						!errorMessage.length ? (
							currentTestimonial ? (
								<>
									<header className="pet-mem-modal__content-header">
										<div className="pet-mem-modal__content-header-title">
											<h4>{`Dr. ${currentTestimonial?.user?.firstName} ${currentTestimonial?.user?.lastName}`}</h4>
										</div>
									</header>
									{currentTestimonial?.avatar ? (
										<div className="pet-mem-modal__content-gallery">
											<CustomAvatar
												src={currentTestimonial.avatar}
												alt={imageAltCapture}
												maxHeight={420}
												errorAvatar={imagePlaceholder}
												className="pet-mem-modal__content-gallery-main"
											/>
										</div>
									) : null}

									<div className="pet-mem-modal__content-topic" style={{ marginTop: '32px' }}>
										<p>{currentTestimonial?.message}</p>
									</div>
									<TesAuthorCredits
										firstName={currentTestimonial?.firstName}
										lastName={currentTestimonial?.lastName}
										city={currentTestimonial?.city}
										state={currentTestimonial?.state}
										date={currentTestimonial?.datePost}
									/>
									<div className="vet-tes-card__look-more">
										{currentTestimonial?.user?.firstName && (
											<>
												{currentTestimonial?.user?.areaUrl && (
													<LinkMore
														linkMoreCaption={`View Dr. ${currentTestimonial?.user?.firstName}'s biography`}
														linkMoreHref={`/find-a-vet/${currentTestimonial?.user?.area?.state?.replace(' ', '-')}/${
															currentTestimonial?.user?.areaUrl
														}/veterinarians?veterinarian=${currentTestimonial?.user?.id}`}
													/>
												)}

												{currentTestimonial.petMemorial && (
													<LinkMore
														linkMoreCaption={`View ${currentTestimonial.petMemorial.petName}'s memorial`}
														linkMoreHref={`/pet-memorial/${currentTestimonial.petMemorial.id}`}
													/>
												)}
											</>
										)}
									</div>
								</>
							) : <div className="format-error-message">Nothing found</div>
						) : <div className="format-error-message">{errorMessage}</div>
					) : <BounceLoader />}
				</div>
			</div>
		</Modal>
	);
};

export default TestimonialCardModal;
